/**
 * KDS Data Table tokens — Figma Kintsugi Design System (CP-2923).
 *
 * Spec page: node 1072:8486 (table templates, headers, cells).
 * Atom canvas: node 302:4510.
 * - Row/cell height: 48px fixed
 * - Cell padding: 12px horizontal (Figma md)
 * - Header: small.medium uppercase, neutral.350; sorted headers neutral.500
 * - Body: default.regular, neutral.400; identifier (`data-identifier`) default.medium + neutral.500; numeric (`data-numeric`) end-aligned; editable (`data-editable`) zero padding + cell-level hover/open states
 * - Cell borders: bottom + right grid (neutral.100); no right border on last column, no bottom on last body row
 * - Outline root: `borders.ready` (neutral.100), `radii.md` (6px); corner clip via cell radii (not root overflow — keeps stickyHeader working in ScrollArea)
 * - Layout: horizontal scroll via `ScrollArea` (`nowrap`); table `min-width: full` + `width: max-content`
 * - Interactive hover: brand.50 row bg; selected row: brand.50
 * - Striped rows: alternating neutral.50 on odd body rows
 * - Sort icons: Figma `arrow-up` (1050:15785) — 16×16, 1.5px stroke, neutral.250; shown only when sorted
 * - Sortable header hover: neutral.50
 * - Empty copy: default.regular, neutral.250 (fg.muted)
 * - Loading overlay: neutral.0/80 backdrop, centered lg brand.500 spinner
 *
 * Template compositions (Toolbar, Pagination, domain rows) on node 1072:8486 — atom canvas 302:4510.
 */

/** Default `Table.Root` size — org/partner use `md`; admin sometimes `sm`. */
export const dataTableDefaultSize = 'md' as const;

export const dataTableSizes = ['sm', 'md'] as const;
export type DataTableSize = (typeof dataTableSizes)[number];

/** Fixed row/cell height from Figma Table Header / Table Cell atoms. */
export const dataTableRowHeight = '48px';

/** Cell / header horizontal padding (Chakra spacing 3 = 12px — Figma md). */
export const dataTableCellPaddingX = 3;

/** Compact size horizontal padding (Chakra spacing 2 = 8px). */
export const dataTableCellPaddingCompactX = 2;

/** Row hover background when `interactive` is set on `Table.Root`. */
export const dataTableInteractiveRowHoverBg = 'brand.50';

/** Selected row background (`data-selected` on `Table.Row`). */
export const dataTableSelectedRowBg = 'brand.50';

/** Alternating row background when `striped` is set on `Table.Root`. */
export const dataTableStripedRowBg = 'neutral.50';

/** Sortable header hover background (`data-sortable` on `ColumnHeader`). */
export const dataTableSortableHeaderHoverBg = 'neutral.50';

/** Border color for table cells, headers, and outline root — Figma `borders.ready` (neutral.100). */
export const dataTableBorderColor = 'neutral.100';

/** Outline `Table.Root` corner radius — Figma template tables use `radii.md` (6px). */
export const dataTableOutlineBorderRadius = 'md' as const;

/** Header label color — Figma Body/Small/CAPS neutral.350. */
export const dataTableHeaderColor = 'neutral.350';

/** Sorted column header label color — Figma neutral.500. */
export const dataTableSortedHeaderColor = 'neutral.500';

/** Body cell text color — Figma Body/Default/Regular neutral.400. */
export const dataTableBodyColor = 'neutral.400';

/** Identifier cell text color — Figma Type=Identifier neutral.500. */
export const dataTableIdentifierColor = 'neutral.500';

/** Empty-state muted text color. */
export const dataTableEmptyTextColor = 'neutral.250';

/** Sort icon stroke color — Figma arrow-up default stroke (#888888 / neutral.250). */
export const dataTableSortIconColor = 'neutral.250';

/** Sort icon size in px — Figma `arrow-up` frame (1050:15785). */
export const dataTableSortIconSizePx = 16;

/** Loading overlay backdrop — Figma white 80% mask via semantic neutral.0. */
export const dataTableLoadingOverlayBg = 'neutral.0/80';

/** Loading overlay spinner color. */
export const dataTableLoadingSpinnerColor = 'brand.500';

/** Editable cell hover background (`data-editable` on `DataTable.Cell`). */
export const dataTableEditableCellHoverBg = 'brand.100';

/** Editable cell open/active background (when the inline select is expanded). */
export const dataTableEditableCellOpenBg = 'neutral.50';

/** Editable cell open/active outline color. */
export const dataTableEditableCellOpenOutlineColor = 'brand.400';
